// @ts-check

import { Component } from '@theme/component';
import { ThemeEvents, CustomizerPhotoEditEvent, CustomizerConfirmRemoveEvent } from '@theme/events';
import { DESIGN_PROOF_PRICE_CENTS } from '@theme/custom.customizer-pricing';
import { presentmentCents } from '@theme/custom.customizer-currency';
import { CUSTOMIZER_DRILL_EVENT } from '@theme/custom.customizer-drill';
import { holdDialogCancel } from '@theme/custom.customizer-layer';
import { formatMoney } from '@theme/money-formatting';
import { track } from '@theme/custom.customizer-analytics';

/** @typedef {import('@theme/custom.customizer-store').CustomizerStore} CustomizerStore */

/** "True desktop" gate (checked per call, so resize is caught): 1024px+ AND hover+fine pointer.
 * Touch, even a 1024px tablet, uses tap-to-edit: auto-focus would pop the keyboard too early. */
const DESKTOP_FOCUS_QUERY = '(min-width: 1024px) and (hover: hover) and (pointer: fine)';

/**
 * One menu registry entry (emitted as JSON by snippets/custom.customizer-engrave.liquid).
 * @typedef {object} MenuEntry
 * @property {string | null} value - The `*_engraving_options` value this entry sets (null for a
 *   group).
 * @property {string} parent - The view "back" returns to from this view.
 * @property {boolean} group - True when the entry drills into child cards (the style chooser)
 *   rather than a control.
 * @property {'menu' | 'style' | 'content' | 'edit' | 'method' | 'design-request'} panel - Which
 *   sub-panel this view shows (`edit` = the photo editor, `method` = the Select Style
 *   inserting-method cards; `design-request` = the Add Design Request notes/proof step;
 *   `menu` on a LEAF entry = a stay-on-menu mode (email-later): selecting it commits the value
 *   with no drill-in — the card marks selected and its `[data-engrave-note-for]` note unhides).
 */

/**
 * @typedef {object} Refs
 * @property {HTMLElement} [notePrompt] - "Photo Need an Adjustment?" prompt on the Edit Photo view.
 * @property {HTMLElement} [noteChip] - Saved design-request chip on the Edit Photo view.
 * @property {HTMLElement} [noteText] - The chip's (clamped) note text.
 * @property {HTMLElement} [noteProof] - The chip's "Proof requested" line, shown while the proof
 *   is on.
 * @property {HTMLElement} [proofPrice] - The proof card's price label, filled from
 *   DESIGN_PROOF_PRICE_CENTS.
 * @property {HTMLElement} [photoPrice] - The resolved face's photo quote.
 * @property {HTMLElement} [noteInput] - The drawer's notes textarea, LOCAL until Save.
 * @property {HTMLElement} [noteCount] - The drawer's n/240 counter.
 * @property {HTMLElement} [proofCard] - The Request Proof toggle card; `aria-pressed` is local
 *   until Save.
 * @property {HTMLElement} [designRequestRemove] - The Remove pill, hidden until a request is saved.
 * @property {HTMLElement} [designRequestSave] - The Save/Update CTA; its label follows the saved
 *   state.
 * @property {HTMLElement} [skip] - The face menu's "No, Thank You" chip; hidden on a required face.
 */

/**
 * Routes the Liquid menu shell using the store's face plan for photo choices and prices.
 * Controls retain their own activity rules; this component owns the current panel and back action.
 *
 * @extends Component<Refs>
 */
class CustomizerEngrave extends Component {
  /** @type {string[]} */
  requiredRefs = [];

  /** Menu registry, view id -> entry. Populated from the emitted JSON on connect.
   * @type {Record<string, MenuEntry>} */
  #registry = {};

  /** @type {CustomizerStore | undefined} */
  #boundStore;

  /** @type {string} */
  #view = 'menu';

  /** Last seen `store.currentStep`, the signal a step navigation happened.
   * @type {string | null} */
  #lastStep = null;

  /** The parent the drill listener went on, which a removed router no longer has.
   *  @type {HTMLElement | null} */
  #drillHost = null;

  /** Last seen value of the face's photo control. Each upload is a fresh object URL, so the
   *  dropzone auto-advances to Edit Photo on a NEW file only. @type {string} */
  #photoValue = '';

  connectedCallback() {
    super.connectedCallback();
    this.#registry = this.#readRegistry();
    document.addEventListener(ThemeEvents.customizerUpdate, this.#onUpdate);
    document.addEventListener('keydown', this.#drawerKeydown, { capture: true });
    document.addEventListener('focusin', this.#drawerFocusIn);
    if (this.#multiArea) {
      this.#drillHost = this.parentElement;
      this.#drillHost?.addEventListener(CUSTOMIZER_DRILL_EVENT, this.#onAreaDrill);
    }
    this.#boundStore = this.#store;
    this.#syncFacePlan();
    this.#setView(this.#viewFromStore());
    this.#lastStep = this.#store?.currentStep ?? null;
    this.#photoValue = this.#currentPhotoValue();
    this.#syncSelected();
    this.#syncSkip();
    if (this.refs.proofPrice) this.refs.proofPrice.textContent = this.#proofPriceText;
    this.#syncDesignRequest();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#registry = this.#readRegistry();
    const replaced = this.#boundStore !== this.#store;
    this.#boundStore = this.#store;
    this.#syncFacePlan();
    this.#setView(replaced ? this.#viewFromStore() : this.#view);
    if (replaced) {
      this.#lastStep = this.#store?.currentStep ?? null;
      this.#photoValue = this.#currentPhotoValue();
    }
    this.#syncSelected();
    this.#syncSkip();
    this.#syncDesignRequest();
    if (this.refs.proofPrice) this.refs.proofPrice.textContent = this.#proofPriceText;
  }

  /** Menu behavior and money follow the same face plan as the store's completion gate. */
  #syncFacePlan() {
    const plan = this.#plan;
    if (!plan) return;
    if (this.#registry.photo) this.#registry.photo.value = plan.photoValue;
    this.toggleAttribute('data-fingerprint', plan.fingerprint);
    for (const card of this.#panel('method')?.querySelectorAll('[data-value]') ?? []) {
      const value = card.getAttribute('data-value') ?? '';
      const offered = !!plan.method?.asked && plan.method.values.includes(value);
      card.toggleAttribute('hidden', !offered);
      card.toggleAttribute('disabled', !offered);
      card
        .querySelector('.custom-customizer-control__card-price')
        ?.toggleAttribute('hidden', !plan.method?.billable);
    }
    const price = this.refs.photoPrice;
    if (price) {
      const { cents, from } = plan.photoQuote;
      const format = price.dataset.moneyFormat || '{{amount}}';
      const money = formatMoney(
        presentmentCents(cents),
        format,
        price.dataset.currency || 'USD',
        true
      );
      price.textContent = `${from ? `${price.dataset.from ?? ''} ` : ''}${price.dataset.prefix ?? '+'}${money}`;
      price.hidden = cents === 0 && !from;
    }
  }

  /**
   * The proof amount comes from DESIGN_PROOF_PRICE_CENTS; formatting follows the other badges.
   * @returns {string}
   */
  get #proofPriceText() {
    const el = this.refs.proofPrice;
    const format = el?.dataset.moneyFormat || '{{amount}}';
    const amount = formatMoney(
      presentmentCents(DESIGN_PROOF_PRICE_CENTS),
      format,
      el?.dataset.currency || 'USD',
      true
    );
    return `${el?.dataset.prefix ?? '+'}${amount}`;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.customizerUpdate, this.#onUpdate);
    document.removeEventListener('keydown', this.#drawerKeydown, { capture: true });
    document.removeEventListener('focusin', this.#drawerFocusIn);
    this.#releaseDrawerCancel();
    this.#releaseDrawerCancel = () => {};
    this.#drillHost?.removeEventListener(CUSTOMIZER_DRILL_EVENT, this.#onAreaDrill);
    this.#drillHost = null;
  }

  /** One mask drilled at a time: another mask's drill sends this one back to its menu.
   * @param {Event} event */
  #onAreaDrill = (event) => {
    const other = event.target;
    // Only a sibling router counts; a drill nested inside this router bubbles past here too.
    if (other === this || !(other instanceof HTMLElement)) return;
    if (other.parentElement !== this.parentElement || !other.hasAttribute('data-drilled')) return;
    if (this.#view !== 'menu') this.toMenu();
  };

  /** @returns {HTMLElement | null} The design-request sheet while it is open on screen. */
  get #openDrawer() {
    if (this.#view !== 'design-request') return null;
    const sheet = this.#panel('design-request')?.querySelector('[role="dialog"]');
    return sheet instanceof HTMLElement && sheet.checkVisibility?.() !== false ? sheet : null;
  }

  /**
   * Escape closes only the drawer. Capture phase on document, as the progress drawer does, or the
   * overlay's dialog takes it and closes the whole customizer.
   * @param {KeyboardEvent} event
   */
  #drawerKeydown = (event) => {
    if (event.key !== 'Escape' || !this.#openDrawer) return;
    event.preventDefault();
    event.stopPropagation();
    this.back();
  };

  /**
   * Keeps Tab inside the drawer while it shows, as aria-modal promises.
   * @param {FocusEvent} event
   */
  #drawerFocusIn = (event) => {
    const sheet = this.#openDrawer;
    if (!sheet || !(event.target instanceof Node) || sheet.contains(event.target)) return;
    const close = sheet.querySelector('.custom-customizer-engrave__drawer-close');
    if (close instanceof HTMLElement) close.focus();
  };

  /** The control that opened the design-request drawer, handed focus back when it closes.
   * @type {HTMLElement | null} */
  #drawerOpener = null;

  /** Lets go of the overlay dialog's `cancel`, held while the drawer is open. */
  #releaseDrawerCancel = () => {};

  /**
   * A view switch hides the control that had focus, which would drop it to <body> and restart Tab
   * from the dialog's top. Runs only when focus was in here, so it never pops a phone keyboard.
   * @param {HTMLElement} previous - What had focus before the switch.
   * @param {boolean} modal - The new view is the design-request drawer.
   * @param {boolean} wasModal - The drawer just closed.
   */
  #rescueFocus(previous, modal, wasModal) {
    requestAnimationFrame(() => {
      // Something already took focus on purpose (select()'s text entry, a step change).
      if (document.activeElement !== previous && document.activeElement !== document.body) return;
      /** @param {Element | null | undefined} el */
      const shown = (el) =>
        el instanceof HTMLElement && el.isConnected && el.checkVisibility?.() !== false;
      /** @param {string} selector */
      const firstShown = (selector) => [...this.querySelectorAll(selector)].find(shown);
      /** @type {Element | null | undefined} */
      let target = null;
      if (modal) {
        target = this.#panel('design-request')?.querySelector(
          '.custom-customizer-engrave__drawer-close'
        );
      } else if (wasModal) {
        // Save swaps the "add note" prompt for the saved-note chip, hiding the opener: its twin
        // takes focus instead.
        target = shown(this.#drawerOpener)
          ? this.#drawerOpener
          : firstShown(
              '.custom-customizer-engrave__add-note, .custom-customizer-engrave__note-edit'
            );
      } else if (shown(previous)) {
        return;
      }
      target ??= firstShown(
        '[data-engrave-view]:not([hidden]) :is(button, [href], input, textarea)'
      );
      if (!modal) this.#drawerOpener = null;
      if (target instanceof HTMLElement) target.focus({ preventScroll: true });
    });
  }

  /** @returns {Record<string, MenuEntry>} The registry emitted by the snippet, or empty on parse
   *  failure. */
  #readRegistry() {
    const script = this.querySelector('script[type="application/json"][data-engrave-menu]');
    if (!script?.textContent) return {};
    try {
      return JSON.parse(script.textContent);
    } catch {
      return {};
    }
  }

  /** @returns {CustomizerStore | undefined} */
  get #store() {
    const host = /** @type {{ store?: CustomizerStore } | null} */ (
      this.closest('custom-customizer')
    );
    return host?.store;
  }

  /** This router's mask on the face; the face's first mask unless the snippet named one. */
  get #plan() {
    return this.#store?.facePlan(this.dataset.stepId ?? '', this.dataset.areaMode || null) ?? null;
  }

  /** One of several masks on its face: finishing it returns to the face menu, not the next step. */
  get #multiArea() {
    return !!this.dataset.areaMode;
  }

  /** @returns {string} */
  get #optionsKey() {
    return this.#plan?.modeKey ?? this.dataset.optionsKey ?? '';
  }

  /**
   * Route to a registry view. Bound generically on every menu/style card via
   * `on:click='/select?view=<id>'`, so adding a card needs no new handler. A leaf entry sets its
   * `*_engraving_options` value (which reveals the bound control); a group entry just shows its
   * panel.
   * @param {{ view?: string }} [data] - Parsed query args from the binding.
   */
  select(data) {
    const view = data?.view;
    if (!view) return;
    const entry = this.#registry[view];
    if (!entry) return;
    const optionsKey = this.#optionsKey;
    const changed = this.#store?.getControlValue(optionsKey) !== entry.value;
    if (entry.value && optionsKey && this.#store?.updateControl(optionsKey, entry.value)) {
      if (changed) track(this, 'choice', { choice: 'engraving_type', choice_value: entry.value });
    }
    // Stay-on-menu leaf (email-later): the value IS the whole interaction, no sub-view to
    // drill into (legacy: the send-method radio simply selects, custom-r2 fields/components.php).
    if (entry.panel === 'menu') return;
    this.#setView(view);
    this.#focusContentEntry(entry);
  }

  /**
   * After an EXPLICIT mode-card selection lands on the content panel, move focus into the revealed
   * control's text entry (CustomizerControl.focusEntry) so the shopper, keyboard users included,
   * can type immediately. Called ONLY from {@link select}: the other #setView paths (step re-entry,
   * Back, toMenu, the photo flow) must never steal focus. Desktop-only per DESKTOP_FOCUS_QUERY; a
   * panel with no text entry makes this a no-op. The rAF lets the panel unhide and the controls'
   * visibleWhen sync settle before focusing.
   * @param {MenuEntry} entry
   */
  #focusContentEntry(entry) {
    if (entry.panel !== 'content') return;
    if (!window.matchMedia(DESKTOP_FOCUS_QUERY).matches) return;
    requestAnimationFrame(() => {
      const controls = this.#panel('content')?.querySelectorAll('custom-customizer-control') ?? [];
      for (const candidate of controls) {
        const control = /** @type {HTMLElement & { focusEntry?: () => boolean }} */ (candidate);
        if (!control.hidden && control.focusEntry?.()) return;
      }
    });
  }

  /** Pop the drill-in to the current view's PARENT (one sub-step): the in-panel breadcrumb
   * (e.g. Select Style -> Edit -> Upload). Bound via `custom-customizer-engrave/back`. */
  back() {
    const view = this.#registry[this.#view]?.parent ?? 'menu';
    this.#setView(view);
    if (view === 'menu' || this.#registry[view]?.group) this.#releaseEmptyMask();
  }

  /** Jump straight to the face MENU (step-header Back, `prev()`), unlike back()'s one-step pop.
   * Entered values persist, so re-entry marks the customized mode selected (#syncSelected). */
  toMenu() {
    this.#setView('menu');
    this.#releaseEmptyMask();
  }

  /** Back on a chooser (menu or style list), a mask left empty is unchosen, so its cards and the
   * face's completion read untouched. Only a leaf view needs its mode to show its control. */
  #releaseEmptyMask() {
    if (this.#multiArea) this.#store?.releaseEmptyMask(this.dataset.stepId ?? '', this.#optionsKey);
  }

  /** Drill-protocol alias (custom.customizer-drill.js): host's `prev()` calls `toRoot()` on
   * whichever `[data-drilled]` component owns the active step; this router's root is its menu. */
  toRoot() {
    this.toMenu();
  }

  /** Face-menu Skip ("No, Thank You"): decline the whole face, then advance. Bound via
   * `on:click='/skip'`; only the menu view renders the button, once per face. */
  skip() {
    if (!this.#store?.declineFace(this.dataset.stepId ?? '')) return;
    const host = /** @type {{ advanceSkipping?: () => void } | null} */ (
      this.closest('custom-customizer')
    );
    // Mirrors the footer Next (custom.customizer.js `advance()`), counted as a skip.
    host?.advanceSkipping?.();
  }

  /** Show the in-menu "No, Thank You" chip only on a face the shopper may decline. */
  #syncSkip() {
    const skip = this.refs.skip;
    const store = this.#store;
    // No store yet (upgrade order): the server-rendered state stands until the store arrives.
    if (!skip || !store) return;
    skip.toggleAttribute('hidden', !store.stepSkippable(this.dataset.stepId ?? ''));
  }

  /** Photo flow "Continue" (the upload landing auto-advances, no button): drill into Edit Photo
   * (crop canvas + Change/B&W/Reset toolbar), else advance when the registry has no edit view. */
  photoContinue() {
    if (this.#registry['photo-edit'] && this.#panel('edit')) {
      this.#setView('photo-edit');
      return;
    }
    this.#finishArea();
  }

  /** The end of a mask's flow: the next step, or the face menu while the face has other masks. */
  #finishArea() {
    if (this.#multiArea) {
      this.toMenu();
      return;
    }
    const host = /** @type {{ advance?: () => void } | null} */ (this.closest('custom-customizer'));
    host?.advance?.();
  }

  /** The Edit Photo confirm, run by the footer Next through {@link advanceIntent}: persists the
   * photo, then selects a style or advances. Design Request uses its own path. */
  photoConfirm() {
    const store = this.#store;
    // Batch the crop, seeded method, and step change to avoid mid-transition re-renders.
    // This also covers flows that skip Select Style.
    if (!store) {
      this.#persistPhotoEdit();
      this.#continuePastPhoto();
      return;
    }
    store.batch(() => {
      this.#persistPhotoEdit();
      this.#continuePastPhoto();
    });
  }

  /** The shared tail of the photo flow: drill into Select Style (Laser/Paper cards) when the
   * face offers one, else there is nothing left to choose, so advance the wizard. */
  #continuePastPhoto() {
    // A reachable default also covers faces that skip Select Style.
    this.#seedMethod();
    if (this.#photoStylePanel()) {
      this.#setView('photo-style');
      return;
    }
    this.#finishArea();
  }

  /** The Select Style panel, when this face has one to drill into. ONE reading answers both where
   * the confirm lands and what the footer CTA promises, so the label cannot outlive the panel.
   * @returns {Element | null}
   */
  #photoStylePanel() {
    return this.#plan?.method?.asked ? this.#panel('method') : null;
  }

  /**
   * Drill protocol (assets/custom.customizer-drill.js): on Edit Photo the footer's Next IS the
   * confirm. Its writes (crop + inserting method) are exactly what the store waits on to call the
   * step complete, so claiming the CTA is what keeps the ONE button live on every photo face
   * instead of shipping a second one beside a permanently dimmed Next.
   * @returns {{ run: () => void, label?: string } | null}
   */
  advanceIntent() {
    const done = this.#multiArea ? this.dataset.areaDoneLabel || undefined : undefined;
    // A mask's drill returns to the face menu, so its other masks are not passed over. A photo
    // still processing keeps the footer's own gate, as it does on a face with one mask.
    if (this.#view !== 'photo-edit') {
      if (!done || this.#view === 'menu' || this.#store?.uploadState(this.#photoKey)) return null;
      return { run: () => this.toMenu(), label: done };
    }
    // The press stays in this step when Select Style follows, so name that, not the next step.
    const label = this.#photoStylePanel()?.getAttribute('data-cta-label') || done;
    return { run: () => this.photoConfirm(), label };
  }

  /* ---- design request ---- */

  /** Live input in the drawer's notes textarea: refresh the counter/filled border only. Value
   * stays LOCAL until Save writes it (leaving without saving discards it, per spec). */
  noteInput() {
    this.#syncNoteField();
  }

  /** Toggle the Request Proof card (+$10) — local until Save, like the note. */
  toggleProof() {
    const card = this.refs.proofCard;
    if (!card) return;
    card.setAttribute('aria-pressed', String(card.getAttribute('aria-pressed') !== 'true'));
  }

  /** Design request Save/Update: commit the local note + proof to the store, keyed by the face's
   * photo upload key ({@link CustomizerStore.setDesignRequest}); editor shows the saved chip. */
  designRequestSave() {
    const key = this.#photoKey;
    const input = this.refs.noteInput;
    if (key) {
      this.#store?.setDesignRequest(key, {
        note: input instanceof HTMLTextAreaElement ? input.value.trim() : '',
        proof: this.refs.proofCard?.getAttribute('aria-pressed') === 'true',
      });
    }
    this.#setView('photo-edit');
  }

  /** Design request Remove: clear the SAVED request, then return to the editor. */
  designRequestRemove() {
    const key = this.#photoKey;
    if (key) this.#store?.setDesignRequest(key, null);
    this.#setView('photo-edit');
  }

  /** (Re)seed the drawer's local fields from the SAVED request on every entry into the view. */
  #populateDesignRequest() {
    const saved = this.#photoKey ? this.#store?.getDesignRequest(this.#photoKey) : null;
    const input = this.refs.noteInput;
    if (input instanceof HTMLTextAreaElement) input.value = saved?.note ?? '';
    this.refs.proofCard?.setAttribute('aria-pressed', String(saved?.proof ?? false));
    this.#syncNoteField();
  }

  /** Refresh the notes field's n/240 counter and filled border from the LOCAL textarea value. */
  #syncNoteField() {
    const input = this.refs.noteInput;
    if (!(input instanceof HTMLTextAreaElement)) return;
    input
      .closest('.custom-customizer-text-field')
      ?.toggleAttribute('data-filled', input.value.length > 0);
    const counter = this.refs.noteCount;
    if (counter && input.maxLength > 0) {
      counter.textContent = `${input.value.length}/${input.maxLength}`;
    }
  }

  /** Reflect the SAVED design request across the Edit Photo view: prompt vs chip, note/proof
   * lines, Remove pill visibility, and the Save/Update CTA label. */
  #syncDesignRequest() {
    const key = this.#photoKey;
    if (!key || !this.#panel('design-request')) return;
    const saved = this.#store?.getDesignRequest(key);
    const noteText = saved?.note ?? '';
    const hasProof = saved?.proof ?? false;
    // ANY saved content counts, since a proof-only request still carries a live surcharge and
    // must stay visible.
    const hasContent = noteText !== '' || hasProof;

    this.refs.notePrompt?.toggleAttribute('hidden', hasContent);
    this.refs.noteChip?.toggleAttribute('hidden', !hasContent);
    if (this.refs.noteText) this.refs.noteText.textContent = noteText;
    const proofLine = this.refs.noteProof;
    if (proofLine) {
      proofLine.toggleAttribute('hidden', !hasProof);
      if (hasProof) {
        proofLine.textContent = `${proofLine.dataset.label ?? ''} (${this.#proofPriceText})`.trim();
      }
    }
    this.refs.designRequestRemove?.toggleAttribute('hidden', !hasContent);

    const save = this.refs.designRequestSave;
    const label = hasContent ? save?.dataset.labelUpdate : save?.dataset.labelSave;
    if (save && label) save.textContent = label;
  }

  /** Confirming the crop is where the edit is written down: crop numbers + B&W flag ride the
   * order as `_<Face> Transform` / `_config.transforms`. */
  #persistPhotoEdit() {
    const key = this.#photoKey;
    const store = this.#store;
    if (!key || !store) return;
    if (!store.getControlTransform(key)) {
      // A confirmed but never-touched photo commits the FACE's default explicitly, so a black &
      // white method rides the order even when the shopper never opened the editor.
      store.setControlTransform(key, store.photoTransform(key));
    }
  }

  /** Commit only a method the shared plan allows the shopper to select. */
  #seedMethod() {
    const store = this.#store;
    const method = this.#plan?.method;
    if (!store || !method?.defaultValue) return;
    const current = store.getControlValue(method.key);
    if (typeof current === 'string' && method.values.includes(current)) return;
    store.updateControl(method.key, method.defaultValue);
  }

  /** "Remove Photo": the REMOVE pill in the Edit Photo and Select Style breadcrumb headers;
   * opens the shared confirm dialog for the face's photo upload. */
  /** On confirm the upload control clears itself; {@link #onUpdate} routes by where the shopper
   * was: the dropzone from Select Style, or the face menu (mode cleared) from the editor. */
  photoRemove() {
    const key = this.#photoKey;
    if (!key) return;
    const host = /** @type {HTMLElement | null} */ (this.closest('custom-customizer'));
    const upload = /** @type {HTMLElement | null} */ (
      this.querySelector(`custom-customizer-control[data-control-key="${key}"]`)
    );
    this.dispatchEvent(
      new CustomizerConfirmRemoveEvent(
        host?.dataset.configId ?? '',
        key,
        upload?.dataset.removeBody ?? null
      )
    );
  }

  /** @returns {string} The resolved shopper upload key, or the initial toolbar target. */
  get #photoKey() {
    const plan = this.#plan;
    if (plan) return plan.uploadKey ?? '';
    const toolbar = /** @type {HTMLElement | null} */ (
      this.querySelector('custom-customizer-photo-edit')
    );
    return toolbar?.dataset.controlKey ?? '';
  }

  /** @returns {string} The face's photo upload control value (the render object URL), or ''. */
  #currentPhotoValue() {
    const key = this.#photoKey;
    const value = key ? this.#store?.getControlValue(key) : null;
    return typeof value === 'string' ? value : '';
  }

  /** Mark the menu/style cards whose mode the shopper committed (`aria-pressed` -> selected
   * border), so they see WHICH option they customized right on the face menu. */
  #syncSelected() {
    const mode = this.#store?.getControlValue(this.#optionsKey);
    const value = typeof mode === 'string' && mode !== '' ? mode : null;
    // Clicking a card re-enters the same flow with every value still in the store: switching
    // modes never clears the other, so an abandoned mode's edits also survive.
    for (const card of this.querySelectorAll('[data-engrave-card]')) {
      const id = card.getAttribute('data-engrave-card') ?? '';
      const entry = this.#registry[id];
      const selected =
        !!value &&
        !!entry &&
        (entry.group
          ? // The group card (Text Engraving) reads selected while any child (message/monogram)
            // is the mode.
            Object.values(this.#registry).some(
              (child) => child.parent === id && child.value === value
            )
          : entry.value === value);
      card.setAttribute('aria-pressed', String(selected));
    }
    // Mode notes: `data-engrave-note-for="<value>"` shows while that mode is the selection,
    // markup-driven like the cards, so a future stay-on-menu mode needs no router change.
    for (const note of this.querySelectorAll('[data-engrave-note-for]')) {
      note.toggleAttribute('hidden', note.getAttribute('data-engrave-note-for') !== value);
    }
  }

  /** @returns {string} The view implied by the stored mode, so re-entry shows existing work. */
  #viewFromStore() {
    // Several masks re-enter on the face menu, where each shows its chosen card.
    if (this.#multiArea) return 'menu';
    const value = this.#store?.getControlValue(this.#optionsKey);
    if (typeof value !== 'string' || !value) return 'menu';
    for (const [view, entry] of Object.entries(this.#registry)) {
      // A stay-on-menu mode re-enters on the menu itself; it has no sub-view to restore.
      if (entry.value === value) return entry.panel === 'menu' ? 'menu' : view;
    }
    return 'menu';
  }

  /** @type {string | null} - The base panel last shown; drives the drill-in scroll reset. */
  #renderedBasePanel = null;

  /** @param {string} view */
  #setView(view) {
    const active = document.activeElement;
    const hadFocus = active instanceof HTMLElement && this.contains(active);
    const wasModal = this.#view === 'design-request';
    if (view === 'photo-style' && !this.#photoStylePanel()) view = 'photo-edit';
    this.toggleAttribute('data-upload-blocked', !!this.#store?.uploadState(this.#photoKey));
    // A replacement keeps the old photo in the store. Show its processing/error UI instead of
    // letting that old value reopen the editor and claim Next ahead of the readiness gate.
    if (
      ['photo-edit', 'photo-style', 'design-request'].includes(view) &&
      this.#store?.uploadState(this.#photoKey)
    )
      view = 'photo';
    this.#view = view;
    this.dataset.view = view;
    // Drill protocol (assets/custom.customizer-drill.js): `data-drilled` past the root view keys
    // the step chrome's Back visibility and the host's Back-to-root routing.
    this.toggleAttribute('data-drilled', view !== 'menu');

    const panel = view === 'menu' ? 'menu' : (this.#registry[view]?.panel ?? 'content');
    // The design-request drawer is a MODAL over its registry parent (the Edit Photo editor), not a
    // swapped sub-view: hiding that base panel collapses the editor and forces a full re-render.
    const modal = panel === 'design-request';
    const basePanel = modal
      ? (this.#registry[this.#registry[view]?.parent ?? '']?.panel ?? 'edit')
      : panel;
    this.#panel('menu')?.toggleAttribute('hidden', basePanel !== 'menu');
    this.#panel('style')?.toggleAttribute('hidden', basePanel !== 'style');
    this.#panel('content')?.toggleAttribute('hidden', basePanel !== 'content');
    this.#panel('edit')?.toggleAttribute('hidden', basePanel !== 'edit');
    this.#panel('method')?.toggleAttribute('hidden', basePanel !== 'method');
    this.#panel('design-request')?.toggleAttribute('hidden', !modal);
    // A back gesture fires the overlay's `cancel` with no keydown, so it closes only the drawer.
    if (modal && !wasModal) this.#releaseDrawerCancel = holdDialogCancel(this, () => this.back());
    if (!modal && wasModal) {
      this.#releaseDrawerCancel();
      this.#releaseDrawerCancel = () => {};
    }
    // Every entry reseeds the drawer from the SAVED request, so edits abandoned without Save are
    // discarded (spec: "closing the drawer without saving will discard input").
    if (modal) this.#populateDesignRequest();

    // A drill swaps this panel's content inside the step's scroll container, so open the new view
    // at its top. Keyed on the BASE panel so the modal drawer never jolts the scroll underneath.
    if (this.#renderedBasePanel !== null && this.#renderedBasePanel !== basePanel) {
      this.closest('.custom-customizer-step__body')?.scrollTo({ top: 0 });
    }
    this.#renderedBasePanel = basePanel;

    // Tells the hero preview's photo editor to take over / hand back the canvas. Keyed off
    // the BASE panel so the editor stays active, no re-render, while the drawer floats over it.
    const photoKey = this.#photoKey;
    if (photoKey) {
      const host = /** @type {HTMLElement | null} */ (this.closest('custom-customizer'));
      this.dispatchEvent(
        new CustomizerPhotoEditEvent(host?.dataset.configId ?? '', photoKey, basePanel === 'edit')
      );
    }

    // A face-first step hides Back, yet a drill past the menu still needs it to reach toMenu; a
    // group drill writes no value, so no customizer:update, hence this bubbling signal.
    this.dispatchEvent(new CustomEvent(CUSTOMIZER_DRILL_EVENT, { bubbles: true }));

    if (hadFocus && modal && !wasModal) this.#drawerOpener = active;
    if (hadFocus) this.#rescueFocus(active, modal && !wasModal, wasModal && !modal);
  }

  /**
   * @param {string} name
   * @returns {Element | null}
   */
  #panel(name) {
    return this.querySelector(`[data-engrave-view="${name}"]`);
  }

  #onUpdate = () => {
    if (this.#boundStore !== this.#store) {
      this.updatedCallback();
      return;
    }
    this.#syncFacePlan();
    this.toggleAttribute('data-upload-blocked', !!this.#store?.uploadState(this.#photoKey));
    this.#syncDesignRequest();
    this.#syncSelected();
    this.#syncSkip();

    // A step navigation must not leave the drill-in standing: re-entry shows the face menu,
    // and the reset hands the canvas back when the step was left from Edit Photo.
    const current = this.#store?.currentStep ?? null;
    if (current !== this.#lastStep) {
      this.#lastStep = current;
      if (this.#view !== 'menu') this.#setView('menu');
    }

    // From the EDIT view the only clearing path is its REMOVE pill, which drops the Photo choice
    // entirely: land on the face menu and clear the mode; elsewhere fall back to the dropzone.
    if (
      this.#view === 'photo-edit' ||
      this.#view === 'photo-style' ||
      this.#view === 'design-request'
    ) {
      const key = this.#photoKey;
      const value = key ? this.#store?.getControlValue(key) : null;
      if (key && this.#store?.uploadState(key)) {
        this.#setView('photo');
      } else if (typeof value !== 'string' || value === '') {
        if (this.#view === 'photo-edit') {
          this.#setView('menu');
          if (this.#optionsKey) this.#store?.updateControl(this.#optionsKey, '');
        } else {
          this.#setView('photo');
        }
      }
    }

    // Advance only for a new upload URL; unrelated changes and breadcrumb returns keep the panel.
    // Scans share this route, while the adapter keeps their raw preview outside the crop editor.
    const photoValue = this.#currentPhotoValue();
    if (this.#view === 'photo' && photoValue !== '' && photoValue !== this.#photoValue) {
      this.photoContinue();
    }
    this.#photoValue = photoValue;
  };
}

if (!customElements.get('custom-customizer-engrave')) {
  customElements.define('custom-customizer-engrave', CustomizerEngrave);
}
